import React from 'react';
import { ErrorMessage } from 'ui-toolkit';
import { useAppDispatch } from '../../../store/store';
import { ThirdPartyContactsPage } from './ThirdPartyContactsPage';
import { IThirdPartyContactData } from '../../../types/contacts/IThirdPartyContact';
import { ClientContactsPage } from './ClientContactsPage';
import { IClientContactData } from '../../../types/contacts/IClientContact';
import { getLenderContactsAction } from '../../../store/order/clientLenderContacts/slices';
import { LenderContactsPage } from './LenderContactsPage';
import { ILenderContactData } from '../../../types/contacts/ILenderContact';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { SimpleGrid } from '../../../tailwindComponents/grid/SimpleGrid';
import { TransactionContactsContainer } from '../../../components/order/contacts/transactionContacts/TransactionContactsContainer';
import { UUID } from '../../../types/shared/UUID';
import { useParams } from 'react-router-dom';
import { useSetOrderNavigationTab } from '../../../utilities/hooks/useSetOrderNavigationTab';
import { InternalContactsContainer } from '../../../components/order/contacts/internalOrderContacts/InternalContactsContainer';
import { useListInternalOrderContactsQuery } from '../../../API/order/contacts/InternalContactsApi';
import { convertToError } from '../../../API/BaseApi';
import { OrderTabs } from 'store/order/orderNavigation/orderNavigationSlice';
import { ClientUsersAPI } from '../../../API/clients/ClientUsersApi';
import {
  useListThirdPartyContactsQuery,
  useAddThirdPartyContactsMutation,
  useUpdateThirdPartyContactsMutation,
  useDeleteThirdPartyContactsMutation,
  useListClientOrderContactsQuery,
  useAddClientOrderContactMutation,
  useUpdateClientOrderContactMutation,
  useDeleteClientOrderContactMutation,
  useListLenderOrderContactsQuery,
  useAddLenderOrderContactMutation,
  useUpdateLenderOrderContactMutation,
  useDeleteLenderOrderContactMutation,
} from '../../../API/order/OrderContactsAPI';
import { useSelector } from 'react-redux';
import { RootStore } from '../../../store/types';

export const OrderContactsContainer: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data } = useSelectCurrentOrder();
  const clientId = data.client.id;
  const lenderId = data.lender.id;
  const dispatch = useAppDispatch();
  useSetOrderNavigationTab({ tab: OrderTabs.Contacts });

  // Select lender contacts from Redux store
  const lenderContactsState = useSelector((store: RootStore) => store.clientLenderContacts.lenderContacts);

  const { error: internalOrderContactsFetchError } = useListInternalOrderContactsQuery(orderId);
  const clientUsersResult = ClientUsersAPI.useListClientUsersQuery(clientId);

  const {
    data: thirdPartyContacts = [],
    error: thirdPartyError,
    isFetching: thirdPartyFetching,
  } = useListThirdPartyContactsQuery(orderId);
  const [addThirdPartyContact, addThirdPartyResult] = useAddThirdPartyContactsMutation();
  const [updateThirdPartyContact, updateThirdPartyResult] = useUpdateThirdPartyContactsMutation();
  const [deleteThirdPartyContact, deleteThirdPartyResult] = useDeleteThirdPartyContactsMutation();

  const {
    data: clientOrderContacts = [],
    error: clientContactError,
    isFetching: clientContactFetching,
  } = useListClientOrderContactsQuery(orderId);
  const [addClientOrderContact, addClientResult] = useAddClientOrderContactMutation();
  const [updateClientOrderContact, updateClientResult] = useUpdateClientOrderContactMutation();
  const [deleteClientOrderContact, deleteClientResult] = useDeleteClientOrderContactMutation();

  const {
    data: lenderOrderContacts = [],
    error: lenderContactError,
    isFetching: lenderContactFetching,
  } = useListLenderOrderContactsQuery(orderId);
  const [addLenderOrderContact, addLenderResult] = useAddLenderOrderContactMutation();
  const [updateLenderOrderContact, updateLenderResult] = useUpdateLenderOrderContactMutation();
  const [deleteLenderOrderContact, deleteLenderResult] = useDeleteLenderOrderContactMutation();

  const onAddNewThirdPartyContact = async (contact: IThirdPartyContactData) => {
    await addThirdPartyContact({ orderId, contact });
  };
  const onResetUpdateThirdParty = () => updateThirdPartyResult.reset();
  const onResetAddThirdParty = () => addThirdPartyResult.reset();

  const onEditThirdPartyContact = async (contact: IThirdPartyContactData) => {
    await updateThirdPartyContact({ orderId, contact });
  };
  const onDeleteThirdPartyContact = async (contactId: string) => {
    await deleteThirdPartyContact({ orderId, contactId });
  };
  const onResetDeleteThirdPartyContact = () => deleteThirdPartyResult.reset();

  const onAddNewClientOrderContact = async (contact: IClientContactData) => {
    await addClientOrderContact({ orderId, contact });
  };
  const onResetAddClientOrderContact = () => addClientResult.reset();

  const onEditClientOrderContact = async (contact: IClientContactData) => {
    await updateClientOrderContact({ orderId, contact });
  };
  const onResetUpdateClientOrderContact = () => updateClientResult.reset();

  const onDeleteClientOrderContact = async (contactId: string) => {
    await deleteClientOrderContact({ orderId, contactId });
  };
  const onResetDeleteClientOrderContact = () => deleteClientResult.reset();

  const onAddNewLenderOrderContact = async (contact: ILenderContactData) => {
    await addLenderOrderContact({ orderId, contact });
  };
  const onResetAddLenderOrderContact = () => addLenderResult.reset();

  const onEditLenderOrderContact = async (contact: ILenderContactData) => {
    await updateLenderOrderContact({ orderId, contact });
  };
  const onResetUpdateLenderOrderContact = () => updateLenderResult.reset();

  const onDeleteLenderOrderContact = async (contactId: string) => {
    await deleteLenderOrderContact({ orderId, contactId });
  };
  const onResetDeleteLenderOrderContact = () => deleteLenderResult.reset();

  const onRequestListOfPreExistingLenderContacts = () => {
    if (lenderId) dispatch(getLenderContactsAction(lenderId));
  };

  return (
    <SimpleGrid>
      <ErrorMessage
        error={
          convertToError(internalOrderContactsFetchError) ??
          convertToError(clientUsersResult.error) ??
          convertToError(thirdPartyError) ??
          convertToError(clientContactError) ??
          convertToError(lenderContactError)
        }
      />
      <TransactionContactsContainer />
      <InternalContactsContainer />
      <ClientContactsPage
        clientContacts={clientOrderContacts}
        loading={clientContactFetching}
        preExistingClientContacts={{
          data: clientUsersResult.data || [],
          fetchStatus: {
            fetching: clientUsersResult.isFetching,
            fetched: clientUsersResult.isSuccess || clientUsersResult.isError,
            success: clientUsersResult.isSuccess,
          },
          fetchError: convertToError(clientUsersResult.error),
          query: clientId,
        }}
        onAddNewContact={onAddNewClientOrderContact}
        onResetAddContact={onResetAddClientOrderContact}
        addFetchStatus={{
          fetching: addClientResult.isLoading,
          fetched: addClientResult.isSuccess || addClientResult.isError,
          success: addClientResult.isSuccess,
        }}
        addFetchError={convertToError(addClientResult.error)}
        onUpdateContact={onEditClientOrderContact}
        onResetUpdateContact={onResetUpdateClientOrderContact}
        updateFetchStatus={{
          fetching: updateClientResult.isLoading,
          fetched: updateClientResult.isSuccess || updateClientResult.isError,
          success: updateClientResult.isSuccess,
        }}
        updateFetchError={convertToError(updateClientResult.error)}
        onDeleteContact={onDeleteClientOrderContact}
        onResetDeleteContact={onResetDeleteClientOrderContact}
        deleteFetchStatus={{
          fetching: deleteClientResult.isLoading,
          fetched: deleteClientResult.isSuccess || deleteClientResult.isError,
          success: deleteClientResult.isSuccess,
        }}
        deleteFetchError={convertToError(deleteClientResult.error)}
      />
      <LenderContactsPage
        lenderContacts={lenderOrderContacts}
        loading={lenderContactFetching}
        preExistingLenderContacts={lenderContactsState}
        onRequestListOfPreExistingLenderContacts={onRequestListOfPreExistingLenderContacts}
        onAddNewContact={onAddNewLenderOrderContact}
        onResetAddContact={onResetAddLenderOrderContact}
        addFetchError={convertToError(addLenderResult.error)}
        addFetchStatus={{
          fetching: addLenderResult.isLoading,
          fetched: addLenderResult.isSuccess || addLenderResult.isError,
          success: addLenderResult.isSuccess,
        }}
        onUpdateContact={onEditLenderOrderContact}
        onResetUpdateContact={onResetUpdateLenderOrderContact}
        updateFetchStatus={{
          fetching: updateLenderResult.isLoading,
          fetched: updateLenderResult.isSuccess || updateLenderResult.isError,
          success: updateLenderResult.isSuccess,
        }}
        updateFetchError={convertToError(updateLenderResult.error)}
        onDeleteContact={onDeleteLenderOrderContact}
        onResetDeleteContact={onResetDeleteLenderOrderContact}
        deleteFetchStatus={{
          fetching: deleteLenderResult.isLoading,
          fetched: deleteLenderResult.isSuccess || deleteLenderResult.isError,
          success: deleteLenderResult.isSuccess,
        }}
        deleteFetchError={convertToError(deleteLenderResult.error)}
      />
      <ThirdPartyContactsPage
        thirdPartyContacts={thirdPartyContacts}
        loading={thirdPartyFetching}
        onAddNewContact={onAddNewThirdPartyContact}
        onResetAddContact={onResetAddThirdParty}
        addFetchStatus={{
          fetching: addThirdPartyResult.isLoading,
          fetched: addThirdPartyResult.isSuccess || addThirdPartyResult.isError,
          success: addThirdPartyResult.isSuccess,
        }}
        addFetchError={convertToError(addThirdPartyResult.error)}
        onUpdateContact={onEditThirdPartyContact}
        onResetUpdateContact={onResetUpdateThirdParty}
        updateFetchStatus={{
          fetching: updateThirdPartyResult.isLoading,
          fetched: updateThirdPartyResult.isSuccess || updateThirdPartyResult.isError,
          success: updateThirdPartyResult.isSuccess,
        }}
        updateFetchError={convertToError(updateThirdPartyResult.error)}
        onDeleteContact={onDeleteThirdPartyContact}
        onResetDeleteContact={onResetDeleteThirdPartyContact}
        deleteFetchStatus={{
          fetching: deleteThirdPartyResult.isLoading,
          fetched: deleteThirdPartyResult.isSuccess || deleteThirdPartyResult.isError,
          success: deleteThirdPartyResult.isSuccess,
        }}
        deleteFetchError={convertToError(deleteThirdPartyResult.error)}
      />
    </SimpleGrid>
  );
};
